import { ModalDefinition } from './../../../services/overlay.service';
/* eslint-disable id-blacklist */
import { Component, Input, SimpleChanges, Output, EventEmitter, OnInit, OnChanges, HostBinding, TemplateRef, ViewChild } from '@angular/core';
import { OverlayService, DialogDefinition } from '../../../services/overlay.service';
import { TranslateService } from '@ngx-translate/core';
import { BbSelectOption } from '../bb-select/bb-select.component';
import { PrebidService } from 'app/services/prebid.service';

export enum DynamicFieldType {
	Array = 'array',
	Bool = 'boolean',
	Int = 'int',
	Json = 'json',
	Select = 'select',
	String = 'string'
}

export enum DynamicFieldSubType {
	Int = 'int',
	String = 'string',
	Bool = 'boolean'
}

export interface DynamicField {
	value?: any;
	defaultValue?: any;
	label: string;
	path: string;
	required: boolean;
	options?: (string | BbSelectOption)[];
	type: DynamicFieldType;
	subType?: DynamicFieldSubType;
	inputType?: string;
}

export enum PrebidSystemType {
	Config = 'config',
	Analytics = 'analytics',
	Module = 'module',
	Adapter = 'adapter'
}

export interface PrebidSystem {
	label: string;
	code: string;
	alias?: string;
	docsLink?: string;
	type: PrebidSystemType;
	dynamicFields?: DynamicField[];
	config?: any;
}

export interface PrebidConfig {
	config: any;
	systems: PrebidSystem[];
}

@Component({
	selector: 'bb-prebid-fields',
	templateUrl: './bb-prebid-fields.component.html',
	styleUrls: ['./bb-prebid-fields.component.scss']
})

export class BbPrebidFieldsComponent implements OnInit, OnChanges {
	@Input() prebidParams: any; // adunit.prebidParams or publication/prebidParams
	@Input() filterParams: any; // optionally for the adunit from publication/prebidParams
	@Input() typeFilter: any; // optionally for filtering on type for example
	@Output() change: EventEmitter<any> = new EventEmitter<any>();
	@Input() mode: 'default' | 'card' = 'default';
	@HostBinding('class.card-mode') get isCardMode() {
		return this.mode === 'card';
	}
	@ViewChild('configFieldsTemplate', { static: true }) configFieldsTemplate: TemplateRef<any>;
	// filteredPrebidSystems: PrebidSystem[];
	// prebidSystems: PrebidSystem[];
	// prebidConfigJSON: PrebidConfig; // from defaults file
	bidderAliases: any = {};
	openModals: any = {};
	currentPrebidSystem: PrebidSystem;
	modal: ModalDefinition;
	logoImages = {
		amx: 'AdaptMX.png',
		adform: 'adform.svg',
		adagio: 'Adagio.svg',
		adhese: 'Adhese.png',
		colossusssp: 'Colossus.jpg',
		conversant: 'Conversant.png',
		emx_digital: 'emx.png',
		epsilon: 'Epsilon(Conversant).png',
		criteo: 'Criteo.svg',
		'freewheel-ssp': 'Freewheel.svg',
		gamma: 'Gamma.png',
		improvedigital: 'ImproveDigital.svg',
		ix: 'IndexExchange.svg',
		medianet: 'Media.net.svg',
		nobid: 'Nobid.png',
		oftmedia: '152Media.svg',
		onetag: 'Onetag.png',
		openx: 'OpenX.svg',
		pubmatic: 'Pubmatic.svg',
		rhythmone: 'RhythmOne.png',
		rubicon: 'Magnite-Rubicon.svg',
		telaria: 'Magnite-Rubicon.svg',
		selectmediavideo: 'SelectMedia.png',
		smartadserver: 'SmartAdserver.png',
		smartx: 'Smartclip-SmartX.svg',
		smilewanted: 'Smilewanted.svg',
		sonobi: 'Sonobi.png',
		spotx: 'SpotX.png',
		teads: 'Teads.svg',
		grid: 'TheMediaGrid.png',
		triplelift: 'TripleLift.svg',
		unruly: 'Unruly.svg',
		vidoomy: 'Vidoomy.png',
		appnexus: 'Xandr.svg',
		yahoossp: 'Yahoo.svg',
		yieldmo: 'yieldmo.png',
	};
	lightLogos = 'criteo gamma oftmedia pubmatic smartx teads vidoomy yieldmo rubicon telaria'.split(' ');

	searchInput = '';
	filter: any = null;
	haveResults = true;

	constructor(
		private translate: TranslateService,
		private overlayService: OverlayService,
		public prebidService: PrebidService
	) { }

	ngOnInit() {
		this.fetchData();
	}

	fetchData() {
		this.prebidService.getPrebidConfig().subscribe((prebidData: PrebidConfig) => this.prebidService.handlePrebidData(prebidData, this.filterParams, this.prebidParams, this.typeFilter));
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.prebidParams && !changes.prebidParams.firstChange) {
			this.fetchData();
		}
		if (changes.typeFilter && !changes.typeFilter.firstChange) {
			this.fetchData();
		}
	}

	onSearch() {
		this.haveResults = this.prebidService.filteredPrebidSystems.filter((system: PrebidSystem) => this.showSystem(system))?.length > 0;
	}

	showSystem(prebidSystem: PrebidSystem) {
		const searchInput = this.searchInput.toLowerCase();
		return this.prebidParams[prebidSystem.code] &&
			(!this.filter || (this.filter.enabled === this.prebidParams[prebidSystem.code].enabled)) &&
			(!searchInput || (prebidSystem.code.toLowerCase().includes(searchInput) || prebidSystem.label.toLowerCase().includes(searchInput))
			);
	}

	initCard() {
		setTimeout(() => {
			this.haveResults = true;
		}, 100);
	}

	closeModals() {
		this.openModals = {};
	}

	setFilter(filter) {
		this.filter = filter;
		if (filter === null) {
			this.searchInput = '';
		}
		this.onSearch();
	}

	openModal(system: PrebidSystem) {
		if (!this.prebidParams[system.code].enabled) {
			this.prebidParams[system.code].enabled = true;
			this.change.emit();
		}
		this.openModals[system.code] = true;
	}

	getCode(system: PrebidSystem) {
		return (system.alias || system.code).replace(/__|_outstream|_instream/g, '');
	}

	getDocumentationUrl(system: PrebidSystem) {
		return system.docsLink || (`https://docs.prebid.org/dev-docs/${ (system.type === 'module' ? 'modules/' : 'bidders/') + this.getCode(system)}`);
	}

	generateCpmAdjustmentField() {
		return {
			label: 'CPM Adjustment Multiplier',
			path: '/bidCpmAdjustmentMultiplier',
			type: DynamicFieldType.Int,
			inputType: 'number',
			required: false
		};
	}

	/**
	 *  Adds a duplicate of a bidder with the ability to configure an alias
	 *
	 *  @param prebidParams - the params for the existing bid adapter
	 *  @param system - the bid adapter to duplicate
	 *  @param alias - the alias the register under
	 */
	createAlias(prebidParams: any, system: PrebidSystem, alias: string) {
		if ([PrebidSystemType.Config, PrebidSystemType.Module, PrebidSystemType.Analytics].includes(system.type) // Not a bidder
			|| this.prebidParams[alias] // Alias already in use
			|| !(/^[A-Za-z0-9]+$/).test(alias)) { // Invalid alias code
			this.overlayService.createToast(this.translate.instant('UNABLE_TO_CREATE_BIDDER_ALIAS'), 'warning');
			return;
		}

		const aliasedParams = JSON.parse(JSON.stringify(prebidParams));
		aliasedParams.code = alias;
		aliasedParams.alias = system.code;

		system = JSON.parse(JSON.stringify(system));
		system.alias = system.code;
		system.code = alias;

		this.prebidParams[alias] = aliasedParams;
		this.prebidService.filteredPrebidSystems.push(system);
	}

	/**
	 * @param code - the code of the aliased bidder
	 */
	deleteAlias(code: string) {
		const config = { entity: this.translate.instant('BIDDER_ALIAS').toLowerCase(), id: code };
		const dialog: DialogDefinition = {
			icon: 'trash-o',
			title: this.translate.instant('DELETE_ENTITY', config),
			contents: this.translate.instant('DELETE_ENTITY_CONFIRM', config),
			onConfirm: () => {
				if (this.prebidParams[code]) {
					this.prebidService.filteredPrebidSystems = this.prebidService.filteredPrebidSystems.filter(system => system.code !== code);
					delete this.prebidParams[code];
					this.change.emit();
				}
			}
		};

		this.overlayService.createDialog(dialog);
	}

	/**
	 * Checks if a Prebid system is enabled on the publication level
	 *
	 * @param system - the system to be checked
	 */
	filterPrebidSystems(system: PrebidSystem) {
		if (this.typeFilter && system.type !== this.typeFilter) {
			return false;
		}
		if (this.filterParams) {
			return this.filterParams && this.filterParams[system.code] && this.filterParams[system.code].enabled;
		} else {
			return true;
		}
	}

	applyFilter(filter: any) {

	}

	/**
	 * Determines the input type for a given dynamic field
	 *
	 * @param - the dynamicField configuration
	 */
	getInputType(dynamicField: DynamicField) {
		const fieldType = dynamicField.type;

		switch (fieldType) {
			default:
			case 'string':
			case 'array':
				return 'text';
				break;
			case 'select':
				return 'select';
				break;
			case 'int':
				return 'number';
				break;
			case 'boolean':
				return 'checkbox';
				break;
			case 'json':
				return 'json';
				break;
		}
	}


	/**
	 * Every field calls this when their value is changed,
	 * this handles any transformations and sets the value to be saved
	 *
	 * @param dynamicField - The dynamicField configuration
	 * @param systemCode - The system for which a value has been changed
	 * @param event - The incoming input event, in the case of bb-check this is simply a boolean
	 *
	 */
	dynamicFieldChanged(dynamicField: DynamicField, systemCode: string, event: any) {
		const target = this.prebidParams && this.prebidParams[systemCode];
		const inputValue = (typeof event === 'boolean') ? event : event && event.target && event.target.value;

		let transformedValue = inputValue;

		if (!target || typeof inputValue === 'undefined') {
			return;
		}

		switch (dynamicField.type) {
			case DynamicFieldType.Int:
				transformedValue = (!!inputValue) ? Number(inputValue) : null;
				break;
			case DynamicFieldType.Select:
				if (dynamicField.subType === DynamicFieldSubType.Int) {
					transformedValue = (!!inputValue) ? Number(inputValue) : null;
				}
				break;
			case DynamicFieldType.Json:
				try {
					transformedValue = JSON.parse(inputValue);
				} catch (err) { }
				break;
			case DynamicFieldType.Array:
				const dataValues = inputValue.split(',');

				transformedValue = dataValues.map((value: any) => {
					if (typeof value === 'string') {
						value.trim();
					}

					if (dynamicField.subType === DynamicFieldSubType.Int) {
						value = (!!value) ? Number(value) : null;
					}

					return value;
				});
				break;
		}

		// Allow deleting values
		if (transformedValue === '' || transformedValue === null || typeof transformedValue === 'undefined') {
			delete target[dynamicField.path];
		} else {
			target[dynamicField.path] = transformedValue;
		}

		this.change.emit();
	}

	systemStatusChange(systemCode: string) {
		this.change.emit();
	}
}
